import React, { useEffect, useState, useMemo } from 'react';
import config from '../../config';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  Button,
  Chip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');

export default function CategoryReport() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const token = sessionStorage.getItem('token');

  // ────────────────────────────────────────────────
  //  FETCH CATEGORIES
  // ────────────────────────────────────────────────
  const fetchCategories = async () => {
    if (!company?.id || !token) {
      console.warn('Missing company ID or token');
      setCategories([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetCategory/${company.id}`,
        {
          method: 'GET', // ← FIXED: changed from POST to GET
          headers: {
            Authorization: `Bearer ${token}`,
            // Content-Type not needed for GET
          },
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      console.log(data);

      let list = [];

      // Handle different possible response shapes
      if (Array.isArray(data)) {
        list = data;
      } else if (data && Array.isArray(data.data)) {
        list = data.data;
      } else if (data && Array.isArray(data.categories)) {
        list = data.categories;
      } else if (data && typeof data === 'object') {
        // fallback attempt
        list = data.result || data.items || data.list || [];
        if (list.length === 0 && Object.keys(data).length > 0) {
          console.warn(
            'API returned object but no recognizable array field:',
            data,
          );
        }
      } else {
        console.warn('Unexpected API response:', data);
      }

      // Final safety net
      if (!Array.isArray(list)) {
        list = [];
      }

      setCategories(list);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ────────────────────────────────────────────────
  //  FILTER & PAGINATION (already good – kept + minor safety)
  // ────────────────────────────────────────────────
  const filteredCategories = useMemo(() => {
    if (!Array.isArray(categories)) {
      console.warn('categories is not an array:', categories);
      return [];
    }

    const searchLower = search.toLowerCase().trim();

    return categories.filter(
      (x) =>
        typeof x?.category_name === 'string' &&
        x.category_name.toLowerCase().includes(searchLower),
    );
  }, [categories, search]);

  const displayedCategories = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredCategories.slice(start, start + rowsPerPage);
  }, [filteredCategories, page, rowsPerPage]);

  // ────────────────────────────────────────────────
  //  EXPORT TO EXCEL
  // ────────────────────────────────────────────────
  const exportToExcel = () => {
    if (filteredCategories.length === 0) {
      toast.info('No categories to export');
      return;
    }

    const formatted = filteredCategories.map((c, index) => ({
      '#': index + 1,
      Category: c.category_name || '',
      Status: c.isActive ? 'Active' : 'Inactive',
      'Created Date': c.createdate || '-',
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Categories');
    XLSX.writeFile(wb, 'CategoryReport.xlsx');
  };

  // ────────────────────────────────────────────────
  //  TOGGLE STATUS
  // ────────────────────────────────────────────────
  const toggleStatus = async (id) => {
    if (!id) return;

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/DeactivateCategory/${id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!res.ok) {
        console.warn(`Deactivate/Activate failed with status ${res.status}`);
      }

      fetchCategories();
    } catch (err) {
      console.error('Toggle status error:', err);
    }
  };

  // ────────────────────────────────────────────────
  //  UPDATE CATEGORY NAME
  // ────────────────────────────────────────────────
  const openEdit = (item) => {
    setEditItem({ ...item });
    setEditOpen(true);
  };

  const updateCategory = async () => {
    if (!editItem?.id || !editItem?.category_name?.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/UpdateCategory/${encodeURIComponent(
          editItem.category_name.trim(),
        )}/${editItem.id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!res.ok) {
        throw new Error(`Update failed: ${res.status}`);
      }

      setEditOpen(false);
      fetchCategories();
    } catch (err) {
      console.error('Update category failed:', err);
      toast.error('Failed to update category. Check console for details.');
    }
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant='h4' fontWeight='bold' mb={3} textAlign='center'>
        Category Report
      </Typography>

      {/* SUMMARY CARD */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              Total Categories
            </Typography>
            <Typography variant='h5' fontWeight={600}>
              {filteredCategories.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* CONTROLS */}
      <Box display='flex' gap={2} mb={3} flexWrap='wrap'>
        <TextField
          label='Search Category'
          size='small'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 280 }}
        />

        <Button
          variant='outlined'
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          disabled={filteredCategories.length === 0}>
          Export to Excel
        </Button>
      </Box>

      {/* MAIN TABLE */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={80}>#</TableCell>
                <TableCell>Category</TableCell>
                <TableCell width={140}>Status</TableCell>
                <TableCell width={220}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {displayedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 8 }}>
                    <Typography color='text.secondary'>
                      {search.trim()
                        ? 'No categories match your search'
                        : 'No categories found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedCategories.map((cat, index) => (
                  <TableRow key={cat.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{cat.category_name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={cat.isActive ? 'Active' : 'Inactive'}
                        color={cat.isActive ? 'success' : 'error'}
                        size='small'
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size='small'
                        variant='contained'
                        color={cat.isActive ? 'warning' : 'success'}
                        onClick={() => toggleStatus(cat.id)}
                        sx={{ mr: 1, minWidth: 100 }}>
                        {cat.isActive ? 'Deactivate' : 'Activate'}
                      </Button>

                      <Button
                        size='small'
                        variant='contained'
                        onClick={() => openEdit(cat)}>
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component='div'
          count={filteredCategories.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* EDIT DIALOG */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Update Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Category Name'
            fullWidth
            variant='outlined'
            value={editItem?.category_name || ''}
            onChange={(e) =>
              setEditItem((prev) => ({
                ...prev,
                category_name: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={updateCategory}
            disabled={!editItem?.category_name?.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
